import {
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Box,
  Link,
  IconButton,
  Stack,
  CircularProgress,
  alpha,
} from "@mui/material";
import { Talent } from "../../types/users";
import { useNavigate, useParams } from "react-router-dom";
import SubtitlesOutlinedIcon from "@mui/icons-material/SubtitlesOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import FilePresent from "@mui/icons-material/FilePresent";
import { nl2br } from "../../utils/helper";
import Edit from "@mui/icons-material/Edit";
import { CardBase, Button, GlassTable } from "../../components/global";
import { useGetTalentDocumentsQuery } from "../../services/talent.service";
import dayjs from "dayjs";

const TalentProfilePanel = ({ data }: { data?: Talent; }) => {
  const navigate = useNavigate();
  const { talentId } = useParams();

  const { data: documents, isLoading: isLoadingDocuments } = useGetTalentDocumentsQuery(
    parseInt(talentId || "0"),
    {
      skip: !talentId,
    }
  );
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <CardBase
          title="About"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">{nl2br(data?.bio || "n/a")}</Typography>
        </CardBase>

        <CardBase
          title="Skills"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          {!data?.skills || data?.skills?.length == 0 ? (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No skills added
            </Typography>
          ) : (
            <Box>
              {data?.skills?.map((it) => (
                <Chip
                  label={it.name}
                  key={it.id}
                  size="small"
                  sx={{
                    mr: 1,
                    mb: 1,
                    backgroundColor: "#F4A259",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#E8944A",
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </CardBase>

        <CardBase
          title="Industries"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          {!data?.business_verticals || data?.business_verticals?.length == 0 ? (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No industries added
            </Typography>
          ) : (
            <Box>
              {data?.business_verticals?.map((it) => (
                <Chip
                  label={it.name}
                  key={it.id}
                  size="small"
                  sx={{
                    mr: 1,
                    mb: 1,
                    backgroundColor: "#F4A259",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#E8944A",
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </CardBase>

        <CardBase
          title="Languages"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          {!data?.languages || data?.languages?.length == 0 ? (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No languages added
            </Typography>
          ) : (
            <Box>
              {data?.languages?.map((it) => (
                <Chip
                  label={it.name}
                  key={it.id}
                  size="small"
                  sx={{
                    mr: 1,
                    mb: 1,
                    backgroundColor: "#F4A259",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#E8944A",
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </CardBase>
        <CardBase
          title="Certificates & Licenses"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          {!data?.certifications || data?.certifications?.length == 0 ? (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No certificates added
            </Typography>
          ) : (
            <List>
              {data?.certifications?.map((it) => (
                <ListItem key={it.id} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <SubtitlesOutlinedIcon sx={{ color: "#F4A259" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 500 }}>
                        {it.certification?.name} ({it.certification?.provider})
                      </Typography>
                    }
                    secondary={`Issued: ${it.issue_date}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardBase>

        <CardBase
          title="Work Experiences"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          {!data?.work_experiences || data?.work_experiences?.length == 0 ? (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No work experiences added
            </Typography>
          ) : (
            <List>
              {data?.work_experiences?.map((it) => (
                <ListItem key={it.id} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <BusinessCenterOutlinedIcon sx={{ color: "#F4A259" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 500 }}>{it.company}</Typography>
                    }
                    secondary={`${it.start_period_year || "?"} - ${it.end_period_year || "?"
                      }`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardBase>

        <CardBase
          title="Educations"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          {!data?.educations || data?.educations?.length == 0 ? (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No educations added
            </Typography>
          ) : (
            <List>
              {data?.educations?.map((it) => (
                <ListItem key={it.id} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <SchoolOutlinedIcon sx={{ color: "#F4A259" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 500 }}>{it.school}</Typography>
                    }
                    secondary={it.field_of_study || ""}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardBase>

        <CardBase
          title="Special Documents"
          useGlassCard={true}
          nested={false}
          sx={{ mt: { xs: 1.5, sm: 2 }, mb: { xs: 0, md: 7 } }}
        >
          {isLoadingDocuments ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !documents || documents.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.disabled", py: 2 }}>
              No documents added
            </Typography>
          ) : (
            <List sx={{ py: 0 }}>
              {documents.map((doc) => {
                const getDocumentTypeLabel = (type: string) => {
                  if (type === "resume") return "Resume";
                  if (type === "legal_id") return "Legal ID";
                  if (type === "driver_license") return "Driver License";
                  if (type === "passport") return "Passport";
                  return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
                };

                return (
                  <ListItem
                    key={doc.id}
                    sx={{
                      px: { xs: 0, sm: 1 },
                      py: 1.5,
                      borderRadius: "8px",
                      mb: 1,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.03)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <FilePresent sx={{ color: "#F4A259" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: 15,
                            }}
                          >
                            {doc.name || "Untitled Document"}
                          </Typography>
                          {(doc.legal_id_sub_type || doc.document_type) && (
                            <Chip
                              label={getDocumentTypeLabel(
                                doc.legal_id_sub_type || doc.document_type
                              )}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                backgroundColor: alpha("#F4A259", 0.1),
                                color: "#F4A259",
                              }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Link
                            href={doc.url}
                            target="_blank"
                            sx={{
                              textDecoration: "none",
                              color: "#F4A259",
                              "&:hover": {
                                textDecoration: "underline",
                                color: "#F4A259",
                              },
                            }}
                          >
                            View Document
                          </Link>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            {dayjs(doc.created_at).format("MMM DD, YYYY")}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardBase>
      </Grid>
      <Grid item xs={12} md={4}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Edit />}
          disabled={!data}
          onClick={() => navigate(`/talents/${data?.id}/edit`)}
          sx={{
            mb: 2,
            backgroundColor: "#F4A259",
            color: "#ffffff",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#E8944A",
            },
            "&:disabled": {
              backgroundColor: "rgba(244, 162, 89, 0.5)",
              color: "rgba(255, 255, 255, 0.7)",
            },
          }}
        >
          Edit Profile
        </Button>

        <CardBase
          title="Contact"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          <GlassTable>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Address 1</TableCell>
                  <TableCell>{data?.physical_address_1 || "n/a"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Address 2</TableCell>
                  <TableCell>{data?.physical_address_2 || "n/a"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>City / Zip</TableCell>
                  <TableCell>
                    {data?.city || "n/a"} / {data?.zip || "n/a"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>State</TableCell>
                  <TableCell>{data?.state || "n/a"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Country</TableCell>
                  <TableCell>{data?.country_id || "n/a"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </GlassTable>
        </CardBase>

        <CardBase
          title="Work Settings"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          <GlassTable>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Work Status</TableCell>
                  <TableCell align="right">
                    {data?.talent?.work_status ? data.talent.work_status.toUpperCase() : "n/a"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Rate</TableCell>
                  <TableCell align="right">
                    {data?.min_pay && data?.max_pay
                      ? `${data.min_pay} - ${data.max_pay}`
                      : data?.min_pay
                        ? `${data.min_pay} - n/a`
                        : data?.max_pay
                          ? `n/a - ${data.max_pay}`
                          : "n/a"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Commute Radius</TableCell>
                  <TableCell align="right">
                    {data?.commute_radius ? `${data.commute_radius} miles` : "n/a"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Background Check</TableCell>
                  <TableCell align="right">
                    {!!data?.allow_background_check ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Drug Test</TableCell>
                  <TableCell align="right">
                    {!!data?.allow_drug_test ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Valid U.S Citizen or Have a Work Visa</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.is_valid_us ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </GlassTable>
        </CardBase>

        <CardBase
          title="Availability"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          <GlassTable>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Monday</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.avail_monday ? "✅" : "❌"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Tuesday</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.avail_tuesday ? "✅" : "❌"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Wednesday</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.avail_wednesday ? "✅" : "❌"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Thursday</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.avail_thursday ? "✅" : "❌"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Friday</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.avail_friday ? "✅" : "❌"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Saturday</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.avail_saturday ? "✅" : "❌"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Sunday</TableCell>
                  <TableCell align="right">
                    {!!data?.talent?.avail_sunday ? "✅" : "❌"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </GlassTable>
        </CardBase>

        <CardBase
          title="Social Medias"
          useGlassCard={false}
          sx={{ mb: 2 }}
        >
          <GlassTable>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>LinkedIn</TableCell>
                  <TableCell>{data?.linkedin || "n/a"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Facebook</TableCell>
                  <TableCell>{data?.facebook || "n/a"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Instagram</TableCell>
                  <TableCell>{data?.instagram || "n/a"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#F4A259" }}>Website</TableCell>
                  <TableCell>{data?.website || "n/a"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </GlassTable>
        </CardBase>
      </Grid>
    </Grid>
  );
};
export default TalentProfilePanel;
